import {
  Folder,
  FolderOpen,
  Plus,
  ChevronRight,
  Trash2,
  Edit,
  Copy,
  Upload,
  Database,
  Download,
  Share2,
  Code,
  Play,
  Link2,
  MessageSquare,
} from 'lucide-react';
import AddRequestSubmenu from './AddRequestKindEntries';
import SelectionContextMenu from './SelectionContextMenu';
import { setShareToChatTarget } from '../../store/uiModalStore';

/** The sidebar's row context menu. Plain function: it runs during render and holds no hooks. */
export function sidebarContextMenu({
  t,
  collections,
  isLocalWorkspace,
  onCreateCollection,
  onImportCollection,
  onExportCollection,
  onRunCollection,
  onCreateRequest,
  onCreateFolder,
  onDuplicate,
  onStartRename,
  onShareCollection,
  onGenerateCode,
  onRevealInExplorer,
  setContextMenu,
  openSubmenu,
  setOpenSubmenu,
  setDeleteConfirm,
  setMovePickerOpen,
  selection,
  contextMenuRef,
  closeContextMenu,
  handleBulkDuplicate,
}) {
  // ── Context menu component ─────────────────────────────────────────────────

  // Plain render function (not a nested component) so it isn't remounted on every
  // Sidebar re-render — a remount would replay `animate-fadeIn` and make the menu blink.
  const renderContextMenu = (menu) => {
    // Early-returned exactly like the collections-header menu below, and for the
    // same reason: the generic body assumes `menu.item` is ONE record and ends in
    // an ungated Delete.
    if (menu.type === 'selection') {
      return (
        <SelectionContextMenu
          x={menu.x}
          y={menu.y}
          menuRef={contextMenuRef}
          summary={selection}
          onDelete={() => { setDeleteConfirm({ kind: 'bulk' }); closeContextMenu(); }}
          onMove={() => { setMovePickerOpen(true); closeContextMenu(); }}
          onDuplicate={() => { handleBulkDuplicate(); closeContextMenu(); }}
        />
      );
    }

    const parentCollection =
      menu.type === 'folder'
        ? collections.find((c) => String(c.id) === String(menu.item.collectionId))
        : menu.type === 'request'
          ? collections.find((c) => (c.requests || []).some((r) => String(r.id) === String(menu.item.id)))
          : null;
    const canEdit =
      menu.type === 'folder'
        ? parentCollection?.can_edit !== false
        : menu.item?.can_edit !== false;

    const handleRename = () => {
      if (canEdit) onStartRename?.(menu.type, menu.item);
      setContextMenu(null);
    };
    const handleDuplicate = () => {
      if (canEdit) onDuplicate?.(menu.type, menu.item);
      setContextMenu(null);
    };
    const handleDelete = () => {
      if (!canEdit) { setContextMenu(null); return; }
      setDeleteConfirm({ type: menu.type, item: menu.item });
      setContextMenu(null);
    };

    // The collections-HEADER menu (the "…" button). Early-returned rather than
    // woven into the generic body below: that body ends in an ungated Delete —
    // correct for every per-item menu, nonsense for a heading — and its other
    // entries all assume `menu.item` is a real collection/folder/request.
    if (menu.type === 'collections-header') {
      const exportable = (collections || []).filter((c) => c && c.id != null);
      return (
        <div
          ref={contextMenuRef}
          data-testid="collections-header-menu"
          className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          style={{ left: menu.x, top: menu.y }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            type="button"
            className="ctx-menu-btn"
            onClick={() => { onCreateCollection?.(); closeContextMenu(); }}
          >
            <Plus size={14} />
            <span>{t('sidebar.newCollection')}</span>
          </button>
          {onImportCollection && (
            <button
              type="button"
              className="ctx-menu-btn"
              onClick={() => { onImportCollection(); closeContextMenu(); }}
            >
              <Upload size={14} />
              <span>{t('sidebar.importCollection')}</span>
            </button>
          )}
          {/* Export needs a TARGET, and a heading has none — so it is a flyout
              of the collections themselves, reusing the share submenu's
              pattern. Hidden entirely when the workspace has nothing to
              export: a disabled entry would beg the question "why", and the
              answer ("you have no collections") is already on screen. */}
          {onExportCollection && exportable.length > 0 && (
            <div
              className="ctx-submenu"
              onMouseEnter={() => setOpenSubmenu('export-collections')}
              onMouseLeave={() => setOpenSubmenu(null)}
            >
              <button
                type="button"
                className="ctx-menu-btn"
                onClick={() => setOpenSubmenu((v) => (v === 'export-collections' ? null : 'export-collections'))}
                onFocus={() => setOpenSubmenu('export-collections')}
              >
                <Download size={14} />
                <span>{t('export.exportCollection')}</span>
                <ChevronRight size={13} className="ctx-submenu-caret" />
              </button>
              {openSubmenu === 'export-collections' && (
                <div
                  className={`ctx-submenu-panel rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 ${
                    menu.x > window.innerWidth - 360 ? 'ctx-submenu-panel--left' : ''
                  }`}
                  style={{ maxHeight: 280, overflowY: 'auto' }}
                >
                  {exportable.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="ctx-menu-btn"
                      title={c.name}
                      onClick={() => { onExportCollection(c); closeContextMenu(); }}
                    >
                      <Database size={14} />
                      <span className="truncate" style={{ maxWidth: 180 }}>{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        ref={contextMenuRef}
        className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        style={{ left: menu.x, top: menu.y }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {(menu.type === 'collection' || menu.type === 'folder') && canEdit && onCreateRequest && (
          <AddRequestSubmenu
            open={openSubmenu === 'add'}
            onOpenChange={(open) => setOpenSubmenu(open ? 'add' : null)}
            flipLeft={menu.x > window.innerWidth - 360}
            onAdd={(optionId) => {
              const colId    = menu.type === 'collection' ? menu.item.id : menu.item.collectionId;
              const folderId = menu.type === 'folder' ? menu.item.id : null;
              onCreateRequest(colId, folderId, optionId);
              setContextMenu(null);
            }}
          />
        )}
        {menu.type === 'collection' && canEdit && onCreateFolder && (
          <button
            type="button"
            onClick={() => { onCreateFolder(menu.item.id, null); setContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <Folder size={14} />
            <span>{t('sidebar.newFolder')}</span>
          </button>
        )}
        {menu.type === 'folder' && canEdit && onCreateFolder && (
          <button
            type="button"
            onClick={() => { onCreateFolder(menu.item.collectionId, menu.item.id); setContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <Folder size={14} />
            <span>{t('sidebar.newSubfolder')}</span>
          </button>
        )}
        <button
          type="button"
          disabled={!canEdit}
          onClick={handleRename}
          className="ctx-menu-btn"
        >
          <Edit size={14} />
          <span>{t('sidebar.rename')}</span>
        </button>
        <button
          type="button"
          disabled={!canEdit}
          onClick={handleDuplicate}
          className="ctx-menu-btn"
        >
          <Copy size={14} />
          <span>{t('sidebar.duplicate')}</span>
        </button>
        {menu.type === 'request' && onGenerateCode && (
          <button
            type="button"
            onClick={() => { onGenerateCode(menu.item, parentCollection); setContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <Code size={14} />
            <span>Generate Code</span>
          </button>
        )}
        {menu.type === 'collection' && onRunCollection && (
          <button
            type="button"
            onClick={() => { onRunCollection(menu.item); setContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <Play size={14} />
            <span>Run Collection</span>
          </button>
        )}
        {menu.type === 'collection' && onExportCollection && (
          <button
            type="button"
            onClick={() => { onExportCollection(menu.item); setContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <Download size={14} />
            <span>{t('export.exportCollection')}</span>
          </button>
        )}
        {menu.type === 'collection' && isLocalWorkspace && onRevealInExplorer && (
          <button
            type="button"
            onClick={() => { onRevealInExplorer(); setContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <FolderOpen size={14} />
            <span>Reveal in File Explorer</span>
          </button>
        )}
        {(menu.type === 'collection' || menu.type === 'request') && (
          <div
            className="ctx-submenu"
            onMouseEnter={() => setOpenSubmenu('share')}
            onMouseLeave={() => setOpenSubmenu(null)}
          >
            <button
              type="button"
              className="ctx-menu-btn"
              onClick={() => setOpenSubmenu((v) => (v === 'share' ? null : 'share'))}
              onFocus={() => setOpenSubmenu('share')}
            >
              <Share2 size={14} />
              <span>{t('sidebar.share')}</span>
              <ChevronRight size={13} className="ctx-submenu-caret" />
            </button>
            {openSubmenu === 'share' && (
              <div
                className={`ctx-submenu-panel rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 ${
                  menu.x > window.innerWidth - 360 ? 'ctx-submenu-panel--left' : ''
                }`}
              >
                {menu.type === 'collection' && onShareCollection && !isLocalWorkspace && (
                  <button
                    type="button"
                    className="ctx-menu-btn"
                    onClick={() => { onShareCollection(menu.item); closeContextMenu(); }}
                  >
                    <Link2 size={14} />
                    <span>{t('sidebar.shareLink')}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="ctx-menu-btn"
                  onClick={() => {
                    setShareToChatTarget({
                      type: menu.type,
                      item: menu.item,
                      parentCollection: parentCollection || null,
                    });
                    closeContextMenu();
                  }}
                >
                  <MessageSquare size={14} />
                  <span>{t('sidebar.shareInChat')}</span>
                </button>
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          disabled={!canEdit}
          onClick={handleDelete}
          className="ctx-menu-btn ctx-menu-btn--danger"
        >
          <Trash2 size={14} />
          <span>{menu.type === 'folder' ? t('sidebar.deleteFolder') : t('common.delete')}</span>
        </button>
      </div>
    );
  };

  return { renderContextMenu };
}
